import { forwardRef, useEffect, useMemo } from 'react';

import { pick } from 'lodash';
import { useParams } from 'react-router-dom';

import { useProposalStore } from '@store/proposal';
import { MultiSelect, Select } from '@ui';
import { MULTI_SELECT_AUTOCOMPLETE } from '@utils/constants';

export interface CountryListProps {
  name?: string;
  label: string;
  excludeIndia?: boolean;
  type?: string;
  disabled?: boolean;
  placeholder?: string;
  fullRow?: boolean;
  withAsterisk?: boolean;
}

//CountryList component renders an autocomplete or multi-autocomplete input field for selecting countries, with an option to exclude India from the list.
//The component fetches the list of countries from the API and displays them in the input field.
export const CountryList = forwardRef<HTMLInputElement, CountryListProps>(
  ({ name, label, type = 'autocomplete', excludeIndia = false, ...props }, ref) => {
    const { fetchCountriesFromBoundaries, countries, countriesSansIndia } =
      useProposalStore((state) =>
        pick(state, [
          'fetchCountriesFromBoundaries',
          'countries',
          'countriesSansIndia'
        ])
      );

    const { product } = useParams();

    const modifiedProduct = product?.toUpperCase().replace(/-/g, '_') || '';

    useEffect(() => {
      fetchCountriesFromBoundaries(modifiedProduct);
    }, [fetchCountriesFromBoundaries, modifiedProduct]);

    const countryData = useMemo(
      () =>
        (excludeIndia ? countriesSansIndia : countries).map(
          (option: { value: string | number | boolean; label: string }) => ({
            ...option,
            value:
              typeof option.value === 'string' ? option.value : String(option.value)
          })
        ),
      [excludeIndia, countriesSansIndia, countries]
    );

    if (type === MULTI_SELECT_AUTOCOMPLETE) {
      return (
        <MultiSelect
          ref={ref}
          w="100%"
          size="xl"
          name={name}
          label={label}
          data={countryData}
          searchable
          {...props}
        />
      );
    }

    return (
      <Select
        ref={ref}
        w="100%"
        size="xl"
        name={name}
        label={label}
        data={countryData}
        searchable
        {...props}
      />
    );
  }
);
